import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type TokenSource } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { ClientReport } from './navigation.ts';

/**
 * Section 13's first group (api/src/routes/client-details.ts): Edit details, the names a client goes
 * by, the identity history, people on the case, and a report marked filed. The server decides who
 * may do each; `may` says what it decided, and every route asks again.
 */

export interface ClientDetailsRow {
  id: string;
  client_ref: string;
  display_name: string;
  last_name: string;
  first_name: string | null;
  middle_name: string | null;
  suffix: string | null;
  date_of_birth: string | null;
  /** Present only with the Legal section. */
  court_name?: string | null;
  court_index_number?: string | null;
  assigned_judge?: string | null;
  primary_language: string | null;
  communication_needs: string | null;
  notes: string | null;
  /** The record's version: sent back with every change, so a stale form is refused. */
  version: string;
}

export interface Alias { id: string; alias_name: string; alias_type: string }

export interface PersonOnCase {
  assignment_id: string;
  role_code: string;
  role_label: string;
  name: string | null;
  credentials: string | null;
  phone: string | null;
  email: string | null;
  appointed_date: string | null;
  started_on: string | null;
}

export type DetailsPanel = 'vital' | 'aliases' | 'court' | 'communication' | 'people' | 'history';

export type IdentifierKind = 'SSN' | 'MEDICAID' | 'MEDICARE';

/** A client's identifiers, masked (120): kind and last four only. Revealing is a separate, audited act. */
export interface Identifiers {
  identifiers: { kind: IdentifierKind; label: string; last4: string | null; recorded_at: string | null;
                 recorded_by: string | null; may_edit: boolean }[];
  may_history: boolean;
  /** False when the key that protects them cannot be reached: they cannot be read or recorded right now. */
  available: boolean;
}

export interface IdentifierEvent { at: string; kind: IdentifierKind; action: 'recorded' | 'changed' | 'removed' | 'revealed';
  last4: string | null; by_name: string | null; reason: string | null }

export interface ClientDetails {
  client: ClientDetailsRow;
  /** The panels this reader may change something in, in order: the page draws these and no others. */
  panels: DetailsPanel[];
  /** Present only for a reader who may see a client's identifiers (120). */
  identifiers: Identifiers | null;
  may: {
    identity: boolean;
    aliases: boolean;
    court: { court_name: boolean; court_index_number: boolean; assigned_judge: boolean } | null;
    communication: boolean;
    people: boolean;
  };
  aliases: Alias[];
  alias_kinds: { code: string; label: string }[];
  suffixes: string[];
  people: PersonOnCase[] | null;
  legal_roles: { role_code: string; role_label: string; notes: string | null }[] | null;
  /**
   * The client's live appointment is a successor's (148): the people page prompts for the prior guardian. Null when the
   * people panel is not drawn for this reader (A-#140).
   */
  successor: boolean | null;
}

export interface IdentityEvent {
  kind: 'correction' | 'alias added' | 'alias ended';
  at: string;
  by_name: string | null;
  reason: string | null;
  prev_last_name: string | null; prev_first_name: string | null; prev_middle_name: string | null;
  prev_suffix: string | null; prev_date_of_birth: string | null;
  new_last_name: string | null; new_first_name: string | null; new_middle_name: string | null;
  new_suffix: string | null; new_date_of_birth: string | null;
  alias_name: string | null; alias_type: string | null; source: string | null;
}

export function useClientDetails(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'details'],
    queryFn: () => apiFetch<ClientDetails>(`/clients/${clientId}/details`, getToken),
    ...queryDefaults,
  });
}

export function useIdentityHistory(clientId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'identity-history'],
    queryFn: () => apiFetch<{ history: IdentityEvent[] }>(`/clients/${clientId}/identity-history`, getToken),
    enabled,
    ...queryDefaults,
  });
}

/** Everything about this client, the client list (names and aliases are searched there) and the toolbar. */
function useAfterWrite(clientId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['client', clientId] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
    void qc.invalidateQueries({ queryKey: ['toolbar'] });
  };
}

function post<T>(path: string, getToken: TokenSource, body: unknown, method = 'POST') {
  return apiFetch<T>(path, getToken, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function useCorrectIdentity(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { last_name: string; first_name: string | null; middle_name: string | null; suffix: string | null;
                      date_of_birth: string | null; reason: string; version: string }) =>
      post<ClientDetails>(`/clients/${clientId}/identity`, getToken, b),
    onSuccess: after,
  });
}

export function usePatchClient(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: Record<string, string | null>) => post(`/clients/${clientId}`, getToken, b, 'PATCH'),
    onSuccess: after,
  });
}

export function useAddAlias(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { alias_name: string; alias_type: string; note: string | null }) =>
      post<ClientDetails>(`/clients/${clientId}/aliases`, getToken, b),
    onSuccess: after,
  });
}

export function useEndAlias(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { alias_id: string; reason: string }) =>
      post<ClientDetails>(`/clients/${clientId}/aliases/${b.alias_id}/end`, getToken, { reason: b.reason }),
    onSuccess: after,
  });
}

export function useAddPerson(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { role_code: string; name: string; phone: string | null; email: string | null;
                      appointed_on: string | null; note: string | null; replaces?: string }) =>
      post<ClientDetails>(`/clients/${clientId}/people`, getToken, b),
    onSuccess: after,
  });
}

export function useEndPerson(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { link_id: string; reason: string | null }) =>
      post<ClientDetails>(`/clients/${clientId}/people/${b.link_id}/end`, getToken, { reason: b.reason }),
    onSuccess: after,
  });
}

/** A report marked filed, or a filing withdrawn (119): the client, the Reports queue and the toolbar's count move. */
function useAfterFiling(clientId: string) {
  const qc = useQueryClient();
  const after = useAfterWrite(clientId);
  return () => {
    after();
    // The Reports queue is an infinite list, keyed ['infinite', 'dashboards', 'compliance', …] (infiniteList.ts):
    // without that prefix a filing left its row showing Withdraw until the page was reloaded.
    void qc.invalidateQueries({ queryKey: ['infinite', 'dashboards', 'compliance'] });
    void qc.invalidateQueries({ queryKey: ['work'] });
  };
}

export function useFileReport(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterFiling(clientId);
  return useMutation({
    // filed_on null: filed, the date not known (156, A-#250)
    mutationFn: (b: { deadline_id: string; filed_on: string | null }) =>
      post<{ reports: ClientReport[] }>(`/clients/${clientId}/reports/${b.deadline_id}/filed`, getToken,
        b.filed_on === null ? { date_unknown: true } : { filed_on: b.filed_on }),
    onSuccess: after,
  });
}

export function useWithdrawFiling(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterFiling(clientId);
  return useMutation({
    mutationFn: (b: { filing_id: string; reason: string }) =>
      post<{ reports: ClientReport[] }>(`/clients/${clientId}/reports/filings/${b.filing_id}/withdraw`, getToken, { reason: b.reason }),
    onSuccess: after,
  });
}

// ---------------------------------------------------------------- identifiers (120)

/**
 * Reveal one number. Its answer is handed to the caller and kept nowhere: no query cache, no log. The
 * database wrote the audit row before the value left it.
 */
export function useRevealIdentifier(clientId: string) {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: IdentifierKind) =>
      post<{ kind: IdentifierKind; label: string; value: string }>(`/clients/${clientId}/identifiers/${kind}/reveal`, getToken, {}),
    // The reveal is in the history the moment it happens; only the history is refreshed, never the value cached.
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['client', clientId, 'identifier-history'] }); },
  });
}

/**
 * The masked numbers alone, for the Client card (16 October): kind and last four, as Edit details has them.
 * Asked only when the summary says this reader may see them.
 */
export function useClientIdentifiers(clientId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'identifiers'],
    queryFn: () => apiFetch<Identifiers>(`/clients/${clientId}/identifiers`, getToken),
    enabled,
    ...queryDefaults,
  });
}

/**
 * Every recorded number at once, for the Client card's "Show secure information". As with one Show, the
 * answer is handed to the caller and kept nowhere, and every number's audit row was committed before any
 * value left the API.
 */
export function useRevealAllIdentifiers(clientId: string) {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ values: { kind: IdentifierKind; label: string; value: string }[] }>(
        `/clients/${clientId}/identifiers/reveal-all`, getToken, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['client', clientId, 'identifier-history'] }); },
  });
}

export function useSetIdentifier(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { kind: IdentifierKind; value: string; reason: string | null }) =>
      post<Identifiers>(`/clients/${clientId}/identifiers/${b.kind}`, getToken, { value: b.value, reason: b.reason }),
    onSuccess: after,
  });
}

export function useRemoveIdentifier(clientId: string) {
  const { getToken } = useSession();
  const after = useAfterWrite(clientId);
  return useMutation({
    mutationFn: (b: { kind: IdentifierKind; reason: string }) =>
      post<Identifiers>(`/clients/${clientId}/identifiers/${b.kind}/remove`, getToken, { reason: b.reason }),
    onSuccess: after,
  });
}

export function useIdentifierHistory(clientId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'identifier-history'],
    queryFn: () => apiFetch<{ events: IdentifierEvent[] }>(`/clients/${clientId}/identifiers/history`, getToken),
    enabled,
    ...queryDefaults,
  });
}
